import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const LOGO_URL = "/nexflow-logo.png";

// Brand color tokens (kept in sync with /app/frontend/src/light-theme.css and tailwind classes)
const BRAND_GOLD = '#D4AF37';   // primary brand gold (used on dark surfaces)
const BRAND_GOLD_DEEP = '#8C7110'; // legible variant on white surfaces
const LIGHT_FG = '#0A0A0A';      // near-black wordmark color on light bg (WCAG AAA on #F7F6F1 / #FFFFFF)

/**
 * Theme-aware brand mark.
 *
 * Logic:
 *  - The yellow "N" is always brand gold.
 *  - "EXFLOW INC." flips to white on dark and to near-black on light.
 *  - Inline styles are used (in addition to Tailwind classes) so the color cannot
 *    be accidentally clobbered by global theme overrides or purged.
 *
 * Override:
 *  - Pass `tone="dark-bg"` when the Logo sits on a container that stays dark in light mode
 *    (e.g. inside the persistent dark sidebar) so the text doesn't go invisible.
 */
const Logo = ({ size = 'md', showTagline = true, tone }) => {
  const { effectiveTheme } = useTheme();
  const sizes = {
    sm: { icon: 'h-8 w-8', n: 'text-xl', tagline: 'text-[8px]' },
    md: { icon: 'h-12 w-12', n: 'text-2xl', tagline: 'text-[10px]' },
    lg: { icon: 'h-16 w-16', n: 'text-4xl', tagline: 'text-xs' },
    xl: { icon: 'h-20 w-20', n: 'text-5xl', tagline: 'text-sm' },
  };
  const s = sizes[size] || sizes.md;

  const isOnLightBg = effectiveTheme === 'light' && tone !== 'dark-bg';
  // Hardcode the color via inline style — most reliable across themes & global CSS overrides.
  const wordmarkColor = isOnLightBg ? LIGHT_FG : '#FFFFFF';
  const goldColor = isOnLightBg ? BRAND_GOLD_DEEP : BRAND_GOLD;
  const taglineColor = isOnLightBg ? '#404040' : '#FFFFFF';

  return (
    <div
      className="inline-flex items-center gap-3"
      data-testid="nexflow-logo"
      data-tone={tone || (isOnLightBg ? 'light-bg' : 'dark-bg')}
    >
      <img src={LOGO_URL} alt="Nexflow Inc." className={`${s.icon} object-contain flex-shrink-0`} />
      <div className="leading-none">
        <div className={`font-['Outfit'] font-black ${s.n} flex items-baseline`}>
          <span style={{ color: goldColor }}>N</span>
          <span style={{ color: wordmarkColor, letterSpacing: '0.01em' }}>EXFLOW</span>
          <span style={{ color: wordmarkColor }} className="ml-2 text-[0.6em]">INC.</span>
        </div>
        {showTagline && (
          <div
            style={{ color: taglineColor }}
            className={`${s.tagline} tracking-[0.2em] mt-1 font-semibold uppercase opacity-90`}
          >
            Premium Plumbing & Drain Cleaning
          </div>
        )}
      </div>
    </div>
  );
};

export default Logo;
